import React, { ReactNode, createContext, useContext, useMemo } from "react";
import { AuthContext } from "context/auth-context/auth.context";
import RolesType from "types/roles.type";

interface SecurityContextType {
  isSuperAdmin: boolean;
  isAdmin: boolean;
  canAccessClient: (clientId: string) => boolean;
  canManageAdmins: boolean;
  canManagePublishing: (clientId: string) => boolean;
  getAccessibleClientIds: () => string[];
  hasPermission: (permission: string) => boolean;
}

const SecurityContext = createContext<SecurityContextType>({
  isSuperAdmin: false,
  isAdmin: false,
  canAccessClient: () => false,
  canManageAdmins: false,
  canManagePublishing: () => false,
  getAccessibleClientIds: () => [] as string[],
  hasPermission: () => false,
});

interface SecurityProviderProps {
  children: ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const { currentAdmin } = useContext(AuthContext);

  const securityContext = useMemo(() => {
    if (!currentAdmin) {
      return {
        isSuperAdmin: false,
        isAdmin: false,
        canAccessClient: () => false,
        canManageAdmins: false,
        canManagePublishing: () => false,
        getAccessibleClientIds: () => [] as string[],
        hasPermission: () => false,
      };
    }

    const isSuperAdmin = currentAdmin.role?.includes(RolesType.SUPER_ADMIN) || false;
    const isAdmin = currentAdmin.role?.includes(RolesType.ADMIN) || false;
    const accessibleClientIds = currentAdmin.roleByClient?.map((role) => role.clientId) || [];

    const canAccessClient = (clientId: string): boolean => {
      if (isSuperAdmin) return true;
      return accessibleClientIds.includes(clientId);
    };

    const canManageAdmins = isSuperAdmin;

    const canManagePublishing = (clientId: string): boolean => {
      if (isSuperAdmin) return true;

      const clientRole = currentAdmin.roleByClient?.find((role) => role.clientId === clientId);
      return (
        clientRole?.role?.includes(RolesType.ADMIN) ||
        clientRole?.role?.includes(RolesType.SUPER_ADMIN) ||
        false
      );
    };

    const getAccessibleClientIds = (): string[] => {
      if (isSuperAdmin) return []; // Return empty array to indicate all clients accessible
      return accessibleClientIds;
    };

    const hasPermission = (permission: string): boolean => {
      // Define permission hierarchy
      const permissions = {
        "admin:read": isAdmin || isSuperAdmin,
        "admin:write": isSuperAdmin,
        "admin:delete": isSuperAdmin,
        "client:read": isAdmin || isSuperAdmin,
        "client:write": isAdmin || isSuperAdmin,
        "client:delete": isSuperAdmin,
        "publishing:manage": isAdmin || isSuperAdmin,
        "user:manage": isSuperAdmin,
      };

      return permissions[permission as keyof typeof permissions] || false;
    };

    return {
      isSuperAdmin,
      isAdmin,
      canAccessClient,
      canManageAdmins,
      canManagePublishing,
      getAccessibleClientIds,
      hasPermission,
    };
  }, [currentAdmin]);

  return <SecurityContext.Provider value={securityContext}>{children}</SecurityContext.Provider>;
};

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error("useSecurity must be used within a SecurityProvider");
  }
  return context;
};
